import { ContentTitle, Subtitle } from '../../../components/typography/Heading';
import { formatNumber } from '../../../utils';
import TrackEventLink from '../../../components/track-event-link/TrackEventLink';
import RoundedButtonPrimary from '../../../components/button/rounded-button-primary/RoundedButtonPrimary';
import RoundedButtonSecondary from '../../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import RoundedButtonLinkSecondary from '../../../components/button/rounded-button-link-secondary/RoundedButtonLinkSecondary';
import { useSelector } from 'react-redux';
import { doesUserOwnSubprojectSelector } from '../../../user/user-selectors';
import { useSubproject } from '../../../components/hooks/useSubproject';
import { useProject } from '../../project-hooks/useProject';
import { useEffect, useState } from 'react';
import ProjectLatestActivity from '../../project-latest-activity/ProjectLatestActivity';
import TransactionsContext from '../../transaction-hooks/transactions-context';
import ModalStickyBottom from '../../../components/modal-sticky-bottom/ModalStickyBottom';
import { intercomEventRaw, useIntercomFunction } from '../../../support-and-tracking/intercom-actions';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import { isProjectADugnad } from '../../project-model';
import { getPublishedRewardsForProject } from '../../projects-reducer';
import { getDugnadProductSoldEquivalentToCollectedAmount } from '../../subproject/subproject-model';
import styles from './SubprojectShow.module.less';

export const SubprojectOwnerHeader = ({ showShare, showQr }) => {
    const { subproject } = useSubproject();
    const intercomEvent = useIntercomFunction(intercomEventRaw);
    const project = useProject();
    const projectRewards = useSelector((state) => getPublishedRewardsForProject(state, project?.id));
    const isDugnad = isProjectADugnad(project);
    const [showGivers, setShowGivers] = useState(false);
    const doesUserOwnSubproject = useSelector((state) => doesUserOwnSubprojectSelector(state, subproject?.id));
    const [equivalentProductsSold, setEquivalentProductsSold] = useState(0);

    useEffect(() => {
        if (doesUserOwnSubproject && subproject?.givers?.length > 1) {
            intercomEvent({ event: 'participant-visited-own-activated-subproject' });
            trackEvent('subproject', 'participant-visited-own-activated');
        }
    }, [doesUserOwnSubproject, subproject?.givers?.length]);

    useEffect(() => {
        if (subproject && projectRewards) {
            setEquivalentProductsSold(
                getDugnadProductSoldEquivalentToCollectedAmount({
                    subproject,
                    projectRewards,
                })
            );
        }
    }, [subproject, projectRewards]);

    if (!doesUserOwnSubproject) {
        return null;
    }

    return (
        <div className={styles.ownerWrapper}>
            <TransactionsContext.Provider value={subproject?.givers}>
                <ModalStickyBottom
                    isOpen={showGivers}
                    onRequestClose={() => setShowGivers(false)}
                    className={styles.transactionModal}
                    showCloseButton={true}
                >
                    <div className={styles.transactionContainer}>
                        <ContentTitle>Din giverliste</ContentTitle>

                        <ProjectLatestActivity title={''} linkToHistory={false} className={styles.transactions} />
                    </div>
                </ModalStickyBottom>
            </TransactionsContext.Provider>
            <ContentTitle className={styles.ownerTitle}>Din {isDugnad && 'salgs'}side</ContentTitle>
            <div className={styles.ownerBanner}>
                <div className={styles.ownerBannerTextContainer}>
                    <p>
                        Til nå har du {isDugnad ? 'solgt for' : 'samlet inn'}&nbsp;
                        <span className={styles.number}>
                            <b>{formatNumber(subproject.sold_in_kr)}</b>
                            &nbsp;kr
                        </span>
                    </p>
                    <p>
                        Det tilsvarer&nbsp;
                        <span className={styles.number}>
                            <b>{formatNumber(equivalentProductsSold)}</b>
                            &nbsp;produkter
                        </span>
                    </p>
                </div>
                {subproject.sold_in_kr > 0 && (
                    <RoundedButtonSecondary onClick={() => setShowGivers(true)}>
                        Se hvem som har bidratt
                    </RoundedButtonSecondary>
                )}
            </div>
            <div className={styles.tips}>
                <TrackEventLink
                    to={'/dugnadsdeltaker'}
                    target="_blank"
                    category={'subproject-admin-tips'}
                    action={'click'}
                >
                    <i className={`far fa-lightbulb`} aria-hidden="true" /> Tips for å lykkes
                </TrackEventLink>
            </div>
            <div className={styles.ownerButtons}>
                <RoundedButtonPrimary
                    trackingCategory={'subproject-admin-share'}
                    trackingAction={'click'}
                    onClick={showShare}
                >
                    Del {isDugnad && 'salgs'}siden
                </RoundedButtonPrimary>
                <RoundedButtonSecondary
                    trackingCategory={'subproject-admin-qr'}
                    trackingAction={'click'}
                    trackingParameters={{
                        'subproject-id': subproject.id,
                        'project-id': project.id,
                        everyone: false,
                    }}
                    onClick={showQr}
                >
                    <i className={'fa-regular fa-qrcode'} /> Vis QR-kode
                </RoundedButtonSecondary>
                <RoundedButtonLinkSecondary
                    category={'subproject-edit'}
                    action={'click'}
                    properties={{ 'subproject-id': subproject.id, 'project-id': project.id }}
                    to={`/project/${project.id}/subproject/${subproject.id}/edit`}
                >
                    <i className={'fa-regular fa-pen-to-square'} /> Endre navn og bilde
                </RoundedButtonLinkSecondary>
            </div>
            <Subtitle className={styles.ownerSubtitle}>Slik ser {isDugnad && 'salgs'}siden din ut 👇</Subtitle>
        </div>
    );
};
